import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { PaperSkeletonComponent } from '@ricoh-paper/angular';
import { PaperPanelContentDirective, PaperPanelHostComponent } from '@ricoh-paper/angular/panels';

import { IssuePanelService } from '../../core/services/issue-panel.service';
import { IssuePanelBodyComponent } from './issue-panel-body.component';

/**
 * im-issue-panel-host — the issue panel over ANY page (IssuePanelService.open), mounted once in the app shell.
 *
 * ARCHITECTURE
 *   - Ricoh Paper's `paper-panel-host` draws it: the overlay frame (the panel-frame recipe), its layer, its slide in
 *     and out, its place in Paper's one Escape order and the focus's return to what opened it. `contentMode="fill"`
 *     makes the content a column exactly the frame's height, so the detail panel pins its summary and its reply
 *     composer and scrolls the thread between them (`.mid-scroll`).
 *   - Home opens issues in its own right-hand panel (it registers in place); everywhere else this floats the panel at
 *     the right under the shell's bar, 392px wide (Home's issue panel). With the shell's column open (Ask, the Inbox)
 *     it docks BESIDE it (`--issue-panel-clear`, published by the shell, is the frame's clear), so Ask stays on screen
 *     and keeps its conversation.
 *   - The body is @defer'red: the detail panel, its mini map and the map engine load on the first open. Paper renders
 *     the content per request, so closing destroys it, its store and its reads, and a new request starts fresh.
 *   - Not a dialog and not a focus trap: the page and Ask stay live beside it, and opening it does not move the focus.
 *     Escape closes it (unless something inside took the key first: a hint, a menu), through Paper's Escape order:
 *     the space panel an issue's place opens OVER it (im-space-panel-host, 505 over this 504) registers there too, so
 *     an Escape while it lies over the issue never closes the issue under it.
 */
@Component({
  selector: 'im-issue-panel-host',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IssuePanelBodyComponent, PaperPanelContentDirective, PaperPanelHostComponent, PaperSkeletonComponent, TranslocoPipe],
  host: { class: 'ph-no-capture' },
  template: `
    <paper-panel-host class="ip-host" [requests]="panel.requests" layout="overlay" [layer]="504" [width]="392"
      contentMode="fill" [autoFocus]="false" [label]="'home.my_issues.detail.header' | transloco">
      <ng-template [paperPanelContent]="panel.requests" let-request>
        <div class="ip-content" data-testid="issue-panel-host">
          @defer (on immediate) {
            <im-issue-panel-body [request]="request.target" (closed)="close()" />
          } @placeholder {
            <div class="ip-loading" role="status" [attr.aria-label]="'home.my_issues.detail.loading' | transloco">
              <paper-skeleton variant="rect" height="120px" />
              <paper-skeleton variant="rect" height="88px" />
            </div>
          }
        </div>
      </ng-template>
    </paper-panel-host>
  `,
  styleUrl: './issue-panel-host.component.scss',
})
export class IssuePanelHostComponent {
  protected readonly panel = inject(IssuePanelService);

  protected close(): void {
    this.panel.close();
  }
}
